import React, { useEffect, useState } from 'react';

import { Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  adminActions,
  adminAiOnboardingAudioBlob,
  adminResetValue,
  getAiOnboardingAudioBlob,
  hasErrorsSelector,
  resetStatus,
  successSelector,
} from '@pharmaplan/common';

import { Constants } from '../../../helpers/Constants';
import strings from '../../../localization';
import useAdmin from '../../../hooks/useAdmin';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import {
  clearPendingAiOnboardingAudioBlobUrl,
  downloadAiOnboardingAudio,
  getPendingAiOnboardingAudioDownload,
  setPendingAiOnboardingAudioDownload,
} from '../../../helpers/adminAiOnboardingAudioDownload';

const AiOnboardingAudioDownload = () => {
  const dispatch = useAppDispatch();
  const { isAdmin } = useAdmin();
  const location = useLocation();
  const navigate = useNavigate();
  const [hasDownloadError, setHasDownloadError] = useState(false);
  const searchParams = new URLSearchParams(location.search);
  const blobUrl = searchParams.get('blobUrl');
  const fileName = searchParams.get('fileName');
  const audioBlob = useAppSelector(adminAiOnboardingAudioBlob);
  const isAudioBlobDownloadSuccessful = useAppSelector((state) =>
    successSelector([adminActions.getAiOnboardingAudioBlob], state));
  const hasAudioBlobDownloadError = useAppSelector((state) =>
    hasErrorsSelector([adminActions.getAiOnboardingAudioBlob], state));

  useEffect(() => {
    if (blobUrl) {
      setPendingAiOnboardingAudioDownload({ blobUrl, fileName });
    }
  }, [blobUrl, fileName]);

  useEffect(() => {
    const pendingDownload = getPendingAiOnboardingAudioDownload();

    if (!pendingDownload) {
      navigate(Constants.paths.login, { replace: true });
      return;
    }

    if (!isAdmin) {
      navigate(Constants.paths.login, { replace: true });
      return;
    }

    dispatch(getAiOnboardingAudioBlob(pendingDownload.blobUrl));
  }, [blobUrl, dispatch, fileName, isAdmin, navigate]);

  useEffect(() => {
    if (!isAudioBlobDownloadSuccessful || !audioBlob) {
      return;
    }

    try {
      const pendingDownload = getPendingAiOnboardingAudioDownload();

      downloadAiOnboardingAudio(audioBlob, pendingDownload?.fileName ?? null);
      clearPendingAiOnboardingAudioBlobUrl();
      dispatch(resetStatus([adminActions.getAiOnboardingAudioBlob]));
      dispatch(adminResetValue(['aiOnboardingAudioBlob']));
      navigate(Constants.paths.admin.adminCalendarDashboard, {
        replace: true,
      });
    } catch {
      setHasDownloadError(true);
    }
  }, [
    audioBlob,
    dispatch,
    isAudioBlobDownloadSuccessful,
    navigate,
  ]);

  useEffect(() => {
    if (hasAudioBlobDownloadError) {
      setHasDownloadError(true);
      dispatch(resetStatus([adminActions.getAiOnboardingAudioBlob]));
    }
  }, [dispatch, hasAudioBlobDownloadError]);

  if (!hasDownloadError) {
    return null;
  }

  return <Typography>{strings.aiOnboardingAudioDownloadError}</Typography>;
};

export default AiOnboardingAudioDownload;
